import React, {
  Suspense,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { Html, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { API_BASE_URL } from '../../constants/config';
import TrajectoryModel from './TrajectoryModel';
import { MTLLoader, OBJLoader } from 'three-stdlib';
import { useLoader } from '@react-three/fiber';
import TrajectoryInfoPanel from './TrajectoryInfoPanel';
import { fetchTrajectoryFiles, fetchDeploymentInfo } from '../../services/api';
import { TrajectoryInfo } from '../../types/types';
import { knownTrajectoryColors } from '../../utils/trajectoryColor';
import { extractDeploymentNumber } from '../../utils/deployment';

interface ForestModelProps {
  selectedDeployment: string;
  selectedInfo: TrajectoryInfo | null;
  setSelectedInfo: React.Dispatch<React.SetStateAction<TrajectoryInfo | null>>;
  selectedSeason: string;
}

interface ModelProps {
  selectedSeason: string;
}

const Model: React.FC<ModelProps> = ({ selectedSeason }) => {
  const materials = useLoader(
    MTLLoader,
    `${API_BASE_URL}/api/model/${selectedSeason}_forest_obj/odm_textured_model.mtl`
  );

  const forestObj = useLoader(
    OBJLoader,
    `${API_BASE_URL}/api/model/${selectedSeason}_forest_obj/odm_textured_model_geo.obj`,
    (loader) => {
      loader.setMaterials(materials);
    }
  );

  const ref = useRef<THREE.Group>(null);
  const { scene } = useThree();

  useLayoutEffect(() => {
    if (!ref.current) return;
    const object = ref.current;

    const recenter = () => {
      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());

      object.position.sub(center);
      object.scale.set(2, 2, 2);
      object.rotation.set(-Math.PI / 24, 0, (-46.5 * Math.PI) / 180);
    };

    recenter();

    requestAnimationFrame(() => recenter());
  }, [forestObj, scene]);

  return <primitive ref={ref} object={forestObj} />;
};

const ForestModel: React.FC<ForestModelProps> = ({
  selectedDeployment,
  selectedInfo,
  setSelectedInfo,
  selectedSeason,
}) => {
  const controlsRef = useRef<any>(null);
  const [trajectoryFiles, setTrajectoryFiles] = useState<string[]>([]);

  const resetCameraView = () => {
    if (controlsRef.current) {
      controlsRef.current.reset();
    }
  };

  const trajectoryRotation = new THREE.Euler(-0.7209, 0.5081, -1.021);

  const handleTrajectoryClick = (fileName: string) => {
    const colorKey =
      Object.keys(knownTrajectoryColors).find((c) =>
        fileName.toLowerCase().startsWith(c)
      ) || 'red';

    const deploymentNumber = extractDeploymentNumber(selectedDeployment);
    if (deploymentNumber === null) {
      console.error('Invalid deployment number');
      return;
    }

    fetchDeploymentInfo(deploymentNumber, colorKey)
      .then((trajectory) => {
        setSelectedInfo(trajectory);
      })
      .catch((err) =>
        console.error('Error while retrieving deployments:', err)
      );
  };

  useEffect(() => {
    if (!selectedDeployment) return;
    setTrajectoryFiles([]);
    fetchTrajectoryFiles(selectedDeployment)
      .then((data) => {
        setTrajectoryFiles(data.trajectories);
      })
      .catch((err) => {
        console.error('Error while retrieving trajectories:', err);
      });
  }, [selectedDeployment]);

  useEffect(() => {
    if (trajectoryFiles.length === 0) return;

    const [firstFolder, firstFile] = trajectoryFiles[0].split('/');

    const simulateClick = async () => {
      await handleTrajectoryClick(firstFile);
      const timer = setTimeout(() => setSelectedInfo(null), 300);
      return () => clearTimeout(timer);
    };

    simulateClick();
  }, [trajectoryFiles, selectedDeployment]);

  return (
    <div className="relative w-full max-w-screen-xl mx-auto h-[50vh] text-black dark:text-white">
      <Canvas
        camera={{ position: [0, -1000, 2500], fov: 45, near: 0.1, far: 10000 }}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = 1.5;
          gl.shadowMap.enabled = true;
        }}
      >
        <ambientLight intensity={0.5} />
        <pointLight position={[0, 0, 0]} intensity={0.7} />
        <Suspense
          fallback={
            <Html center>
              <div className="text-white text-center bg-black/70 p-4 rounded">
                Loading 3D model...
              </div>
            </Html>
          }
        >
          <Model selectedSeason={selectedSeason} />
          {trajectoryFiles.map((filePath) => {
            const [folder, fileName] = filePath.split('/');
            return (
              <TrajectoryModel
                key={`${folder}-${fileName}`}
                deploymentFolder={folder}
                fileName={fileName}
                selectedSeason={selectedSeason}
                selectedInfo={selectedInfo}
                rotation={trajectoryRotation}
                onClick={() => handleTrajectoryClick(fileName)}
              />
            );
          })}
        </Suspense>
        <OrbitControls
          ref={controlsRef}
          enableDamping
          dampingFactor={0.05}
          minDistance={0.1}
          maxDistance={10000}
          enablePan
          enableZoom
          enableRotate
          zoomSpeed={2}
        />
      </Canvas>
      <button
        onClick={resetCameraView}
        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-6 py-2 rounded-md shadow-lg hover:bg-blue-700 flex items-center space-x-2"
      >
        <span>Reset camera</span>
      </button>
      {selectedInfo && (
        <TrajectoryInfoPanel
          color={selectedInfo.color}
          length={selectedInfo.length}
          conditions={selectedInfo.conditions ?? {}}
          onClose={() => setSelectedInfo(null)}
        />
      )}
    </div>
  );
};

export default ForestModel;
